import { useState } from 'react';
import { Shield, ChevronDown, ChevronUp, AlertTriangle } from '@/utils/icons';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { InfoTooltip } from '@/components/common/InfoTooltip';
import type { DeploymentStats } from '@/types/metrics';

interface CertExpiryAlertProps {
  certExpiry: DeploymentStats['certExpiry'];
}

function expiryBgColor(days: number): string {
  if (days < 30) return 'dls-bg-status-offline-quarternary dls-text-status-offline';
  if (days < 90) return 'dls-bg-status-warning-quarternary dls-text-status-warning';
  return 'dls-bg-status-warning-quarternary dls-text-status-warning';
}

export function CertExpiryAlert({ certExpiry }: CertExpiryAlertProps) {
  const [expanded, setExpanded] = useState(true);
  const urgentCount = certExpiry.items.filter((i) => i.daysUntilExpiry < 30).length;

  return (
    <Card
      className={`dls-flex dls-flex-col dls-h-full ${urgentCount > 0 ? 'dls-border-status-offline-tertiary dls-bg-status-offline-quarternary/50' : 'dls-border-status-warning-tertiary dls-bg-status-warning-quarternary/50'}`}
    >
      <CardHeader className="dls-pb-2">
        <CardTitle className="dls-flex dls-items-center dls-justify-between dls-text-base">
          <div
            className={`dls-flex dls-items-center dls-gap-2 ${urgentCount > 0 ? 'dls-text-status-offline' : 'dls-text-status-warning'}`}
          >
            <Shield className="dls-h-4.5 dls-w-4.5" />
            <span>
              {certExpiry.expiringCount} Certificate{certExpiry.expiringCount !== 1 ? 's' : ''}{' '}
              Expiring Soon
            </span>
            <InfoTooltip text="Device certificates expiring within 6 months. Expired certificates will prevent nodes from authenticating with the platform. Red = under 30 days, amber = under 90 days, yellow = under 180 days." />
          </div>
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="dls-text-muted-foreground hover:dls-text-foreground"
          >
            {expanded ? <ChevronUp className="dls-h-4 dls-w-4" /> : <ChevronDown className="dls-h-4 dls-w-4" />}
          </button>
        </CardTitle>
      </CardHeader>
      {expanded && (
        <CardContent className="dls-pt-0 dls-flex dls-flex-col dls-flex-1 dls-gap-3">
          <div className="dls-max-h-64 dls-overflow-y-auto">
            <table className="dls-w-full dls-text-xs">
              <thead>
                <tr className="dls-text-muted-foreground dls-border-b">
                  <th className="dls-text-left dls-py-1 dls-font-medium">S/N</th>
                  <th className="dls-text-left dls-py-1 dls-font-medium">Expires</th>
                  <th className="dls-text-center dls-py-1 dls-pr-2 dls-font-medium">Days</th>
                </tr>
              </thead>
              <tbody>
                {certExpiry.items.map((item) => (
                  <tr key={item.hardwareSerialNumber} className="dls-border-b last:dls-border-0">
                    <td className="dls-py-1.5">
                      <div className="dls-font-mono">{item.hardwareSerialNumber}</div>
                      <div className="dls-text-muted-foreground">
                        {item.isAssigned ? (item.nodeName ?? 'Assigned') : 'Spare'}
                      </div>
                    </td>
                    <td className="dls-py-1.5 dls-text-muted-foreground">
                      {new Date(item.certNotAfter).toLocaleDateString()}
                    </td>
                    <td className="dls-py-1.5 dls-text-center dls-pr-2">
                      <span
                        className={`dls-inline-flex dls-items-center dls-rounded-full dls-px-2 dls-py-0.5 dls-text-xs dls-font-medium ${expiryBgColor(item.daysUntilExpiry)}`}
                      >
                        {item.daysUntilExpiry}d
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="dls-flex dls-items-center dls-gap-2 dls-rounded-md dls-bg-status-offline-quarternary dls-p-2 dls-border dls-border-status-offline-tertiary dls-text-xs dls-text-status-offline dls-mt-auto dls-flex-shrink-0">
            <AlertTriangle className="dls-h-3.5 dls-w-3.5 dls-flex-shrink-0" />
            <span>
              Devices must be brought online before certificate expiry to auto-renew. An expired
              certificate renders the device inoperable and requires return to Neeve for reflashing.
            </span>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
